import { extendTheme } from "@chakra-ui/react";
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  dimensions,
} from "./theme/tokens";

const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
  space: spacing.scale,
  fonts: {
    body: typography.fontFamily.body,
    heading: typography.fontFamily.display,
  },
  fontSizes: {
    xs: "12px",
    sm: "14px",
    md: "16px",
    lg: "20px",
    xl: "24px",
    "2xl": "32px",
    "3xl": "48px",
  },
  lineHeights: {
    tight: 1.3,
    normal: 1.5,
    relaxed: 1.65,
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  radii: {
    none: 0,
    sm: borderRadius.sm,
    md: borderRadius.md,
    lg: borderRadius.lg,
    xl: borderRadius.xl,
    full: borderRadius.full,
    card: borderRadius.card,
    button: borderRadius.button,
  },
  sizes: {
    container: {
      md: "48rem",
      lg: "62rem",
      xl: "75rem",
      "2xl": "88rem",
      max: "1200px",
    },
  },
  shadows,
  styles: {
    global: {
      "html, body": {
        bg: colors.background.primary,
        color: colors.text.primary,
        fontFamily: typography.fontFamily.body,
        fontSize: "md",
        lineHeight: "normal",
        minHeight: "100%",
      },
      "*, *::before, *::after": {
        borderColor: colors.border.default,
      },
      "::selection": {
        backgroundColor: `${colors.brand.primary}55`,
        color: colors.text.primary,
      },
      "p, li": {
        lineHeight: "relaxed",
      },
    },
  },
  components: {
    Tooltip: {
      baseStyle: {
        bg: colors.background.card,
        color: colors.text.primary,
        borderRadius: borderRadius.sm,
        px: spacing.sm,
        py: spacing.xs,
        fontSize: "sm",
      },
    },
    Button: {
      baseStyle: {
        fontWeight: 600,
        borderRadius: borderRadius.button,
        transition: transitions.default,
        fontFamily: typography.fontFamily.display,
        fontSize: "sm",
        lineHeight: "20px",
        px: spacing.md,
        py: spacing.sm,
        height: dimensions.button.height,
        _active: {
          transform: "scale(0.98)",
        },
      },
      variants: {
        primary: {
          bg: colors.brand.primary,
          color: colors.text.inverted,
          border: "none",
          _hover: { bg: colors.brand.primaryHover, transform: "translateY(-1px)" },
          _active: { bg: colors.brand.primaryActive, transform: "translateY(0)" },
          _disabled: {
            bg: colors.state.disabled,
            color: colors.text.secondary,
            opacity: 0.6,
            cursor: "not-allowed",
          },
        },
        secondary: {
          bg: colors.background.card,
          color: colors.text.primary,
          border: "1px solid",
          borderColor: colors.border.default,
          _hover: { bg: colors.state.hover, borderColor: colors.brand.primary },
          _active: { bg: colors.state.active },
        },
      },
      defaultProps: {
        variant: "primary",
      },
    },
    Card: {
      baseStyle: {
        bg: colors.background.card,
        borderRadius: borderRadius.card,
        boxShadow: shadows.card,
        transition: transitions.default,
        overflow: "hidden",
        _hover: {
          transform: "translateY(-4px)",
          boxShadow: shadows.lg,
        },
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: typography.fontFamily.display,
        fontWeight: 600,
        color: colors.text.primary,
        lineHeight: "tight",
      },
    },
    Text: {
      baseStyle: {
        fontFamily: typography.fontFamily.body,
        color: colors.text.secondary,
        lineHeight: "relaxed",
      },
      variants: {
        primary: { color: colors.text.primary },
        secondary: { color: colors.text.secondary },
        muted: { color: colors.text.secondary, opacity: 0.7 },
        accent: { color: colors.brand.primary },
      },
    },
    Input: {
      baseStyle: {
        field: {
          bg: colors.background.card,
          border: "1px solid",
          borderColor: colors.border.default,
          borderRadius: borderRadius.sm,
          color: colors.text.primary,
          fontFamily: typography.fontFamily.body,
          fontSize: "md",
          px: spacing.md,
          py: spacing.md,
          minH: "48px",
          transition: transitions.default,
          _hover: { borderColor: colors.brand.primary },
          _focus: {
            borderColor: colors.brand.primary,
            boxShadow: `0 0 0 1px ${colors.brand.primary}`,
          },
        },
      },
    },
    Container: {
      baseStyle: {
        maxWidth: "1200px",
        px: { base: spacing.lg, md: spacing.xl, lg: spacing[6] },
        mx: "auto",
      },
    },
  },
  colors: {
    brand: {
      ...colors.success,
      primary: colors.brand.primary,
      periwinkle: colors.brand.periwinkle,
      maroon: colors.brand.maroon,
    },
    text: colors.text,
    background: colors.background,
    border: colors.border,
    success: colors.success,
    error: colors.error,
    warning: colors.warning,
  },
  semanticTokens: {
    colors: {
      "chakra-body-bg": { default: colors.background.primary, _dark: colors.background.primary },
      "chakra-body-text": { default: colors.text.primary, _dark: colors.text.primary },
      canvas: { default: colors.background.primary, _dark: colors.background.primary },
      surface: { default: colors.background.card, _dark: colors.background.card },
      surfaceElevated: { default: colors.background.elevated, _dark: colors.background.elevated },
      borderSubtle: { default: colors.border.subtle, _dark: colors.border.subtle },
      accent: { default: colors.brand.primary, _dark: colors.brand.primary },
    },
  },
});

export default theme;
